import JSZip from 'jszip'
import { QueryClient } from 'react-query'
import convertToTiff from 'components/shared/utils'
import { getCellImage } from './api'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
})

export interface CellImageData {
  cellTime: number
  cellNumber: number
  instrumentId: number
  cellFrame?: number
}

export interface ZipFolderStructure {
  folderName: string
  cells: CellImageData[]
}

/**
 * Fetch a cell image from the API
 */
const fetchCellImage = async (cellData: CellImageData): Promise<string> => {
  const { cellNumber, cellTime, instrumentId, cellFrame = 0 } = cellData
  const cellIdParams = { number: cellNumber, time: cellTime, instrumentId, frame: cellFrame }

  const responseCellImage = await queryClient.fetchQuery(
    ['getCellImageById', cellIdParams],
    getCellImage,
    {
      staleTime: 0,
    }
  )

  return responseCellImage
}

/**
 * Parse a cell ID string into CellImageData
 * Supports formats:
 * - "cellTime #cellNumber instrumentId" (from Cell Browser)
 * - "cellTime_instrumentId_cellNumber_cellFrame" (from Cell Visualizations)
 */
export const parseCellId = (cellId: string): CellImageData => {
  // Format: "cellTime #cellNumber instrumentId"
  if (cellId.includes('#')) {
    const [cellTime, cellNumber, instrumentId] = cellId.replace('#', '').split(' ').map(Number)
    return {
      cellTime,
      cellNumber,
      instrumentId,
    }
  }

  // Format: "cellTime_instrumentId_cellNumber_cellFrame"
  if (cellId.includes('_')) {
    const [cellTime, instrumentId, cellNumber, cellFrame] = cellId.split('_').map(Number)
    return {
      cellTime,
      cellNumber,
      instrumentId,
      cellFrame,
    }
  }

  throw new Error(`Unable to parse cell ID: ${cellId}`)
}

/**
 * Generate a ZIP file containing cell images
 *
 * @param cells - Array of cell IDs (as strings) or CellImageData objects
 * @param fileType - Image format ('png' or 'tiff')
 * @param onProgress - Optional callback for progress updates (0-100)
 * @returns Blob containing the ZIP file
 */
export const generateCellImagesZip = async (
  cells: (string | CellImageData)[],
  fileType: 'png' | 'tiff' = 'png',
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  const zip = new JSZip()
  const totalCells = cells.length

  // Create promises for all image fetches
  const imagePromises = cells.map(async (cell, index) => {
    const cellData = typeof cell === 'string' ? parseCellId(cell) : cell
    const { cellTime, cellNumber, instrumentId } = cellData

    try {
      // Fetch the image
      const base64Image = await fetchCellImage(cellData)
      
      // Validate that we got a valid base64 image string
      if (!base64Image || typeof base64Image !== 'string' || !base64Image.includes(',')) {
        throw new Error(`Invalid image data returned for cell ${cellTime} ${cellNumber}`)
      }
      
      const base64String = base64Image.split(',')[1]

      // Add to ZIP
      const fileName = `${cellTime} ${cellNumber} ${instrumentId}.${fileType}`
      if (fileType === 'png') {
        zip.file(fileName, base64String, { base64: true })
      } else {
        zip.file(fileName, convertToTiff(base64String))
      }

      // Report progress
      if (onProgress) {
        const progress = Math.round(((index + 1) / totalCells) * 100)
        onProgress(progress)
      }
    } catch (error) {
      console.error(`Failed to fetch image for cell ${cellTime} ${cellNumber}:`, error)
      // Continue with other cells
    }
  })

  // Wait for all images to be fetched
  await Promise.all(imagePromises)

  // Generate the ZIP blob
  const blob = await zip.generateAsync({ type: 'blob' })
  return blob
}

/**
 * Generate a ZIP file with folder structure
 *
 * @param folders - Array of folders with cells
 * @param fileType - Image format ('png' or 'tiff')
 * @param onProgress - Optional callback for progress updates (0-100)
 * @returns Blob containing the ZIP file
 */
export const generateCellImagesZipWithFolders = async (
  folders: ZipFolderStructure[],
  fileType: 'png' | 'tiff' = 'png',
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  const zip = new JSZip()

  // Calculate total cells for progress tracking
  const totalCells = folders.reduce((sum, folder) => sum + folder.cells.length, 0)
  let processedCells = 0

  // Create promises for all folders
  const folderPromises = folders.map(async (folder) => {
    const { folderName, cells } = folder
    const zipFolder = zip.folder(folderName)

    // Create promises for all cells in this folder
    const cellPromises = cells.map(async (cellData) => {
      const { cellTime, cellNumber, instrumentId } = cellData

      try {
        // Fetch the image
        const base64Image = await fetchCellImage(cellData)
        
        // Validate that we got a valid base64 image string
        if (!base64Image || typeof base64Image !== 'string' || !base64Image.includes(',')) {
          throw new Error(`Invalid image data returned for cell ${cellTime} ${cellNumber}`)
        }
        
        const base64String = base64Image.split(',')[1]

        // Add to ZIP folder
        const fileName = `${cellTime} ${cellNumber} ${instrumentId}.${fileType}`
        if (fileType === 'png') {
          zipFolder?.file(fileName, base64String, { base64: true })
        } else {
          zipFolder?.file(fileName, convertToTiff(base64String))
        }

        // Report progress
        processedCells += 1
        if (onProgress) {
          const progress = Math.round((processedCells / totalCells) * 100)
          onProgress(progress)
        }
      } catch (error) {
        console.error(`Failed to fetch image for cell ${cellTime} ${cellNumber}:`, error)
        // Continue with other cells
      }
    })

    // Wait for all cells in this folder
    await Promise.all(cellPromises)
  })

  // Wait for all folders to be processed
  await Promise.all(folderPromises)

  // Generate the ZIP blob
  const blob = await zip.generateAsync({ type: 'blob' })
  return blob
}

/**
 * Generate filename with timestamp
 */
export const generateZipFileName = (prefix: string): string => {
  const timestamp = Date.now().toString()
  return `${prefix}-${timestamp}.zip`
}
